import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Plus, Trash2, X, Edit3 } from "lucide-react";
import { toast } from "sonner";

const SCOPE_LABELS = {
  default: "Default",
  agent: "Per agente",
  area: "Per area geografica",
  customer_type: "Per tipo cliente",
  promo: "Promo",
};

export default function PriceLists() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", scope: "default", scope_value: "", discount_pct: 0, min_margin_pct: 15, max_discount_pct: 10, tax_rate: 0.10, active: true });

  const reload = () => api.get("/price-lists").then((r) => setList(r.data));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      await api.post("/price-lists", form);
      toast.success("Listino creato");
      setShowForm(false); setForm({ ...form, name: "", scope_value: "" }); reload();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare il listino?")) return;
    await api.delete(`/price-lists/${id}`); reload();
  };
  const toggle = async (p) => {
    await api.patch(`/price-lists/${p.id}`, { active: !p.active });
    reload();
  };

  return (
    <div data-testid="page-price-lists">
      {!showForm && (
        <button onClick={() => setShowForm(true)} className="px-4 py-2.5 bg-zinc-900 text-white font-semibold rounded-xl text-sm flex items-center gap-2 mb-4" data-testid="add-list-btn">
          <Plus size={14}/> Nuovo listino
        </button>
      )}
      {showForm && (
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="font-semibold">Nuovo listino</div>
            <button onClick={() => setShowForm(false)} className="text-zinc-500"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Nome</label>
              <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid="list-name"/>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tipo</label>
              <select value={form.scope} onChange={(e) => setForm({...form, scope: e.target.value})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm">
                {Object.entries(SCOPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Riferimento</label>
              <input placeholder="es. Mario, Lombardia, business" value={form.scope_value} onChange={(e) => setForm({...form, scope_value: e.target.value})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Sconto %</label>
              <input type="number" step="0.1" value={form.discount_pct} onChange={(e) => setForm({...form, discount_pct: parseFloat(e.target.value) || 0})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm font-mono"/>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Margine min %</label>
              <input type="number" step="0.1" value={form.min_margin_pct} onChange={(e) => setForm({...form, min_margin_pct: parseFloat(e.target.value) || 0})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm font-mono"/>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Sconto max %</label>
              <input type="number" step="0.1" value={form.max_discount_pct} onChange={(e) => setForm({...form, max_discount_pct: parseFloat(e.target.value) || 0})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm font-mono"/>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">IVA</label>
              <input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({...form, tax_rate: parseFloat(e.target.value) || 0})} className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm font-mono"/>
            </div>
          </div>
          <button onClick={save} className="mt-4 bg-zinc-900 text-white font-semibold rounded-xl px-5 py-2.5 text-sm" data-testid="list-save-btn">Salva listino</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.length === 0 && <div className="col-span-full text-center text-zinc-500 py-12">Nessun listino. Crea il primo per gestire margini e sconti per agente, area o tipo cliente.</div>}
        {list.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200/70 rounded-2xl p-5" data-testid="pricelist-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-600">{SCOPE_LABELS[p.scope] || p.scope}{p.scope_value ? ` · ${p.scope_value}` : ""}</div>
                <div className="font-semibold text-lg leading-tight mt-0.5">{p.name}</div>
              </div>
              <button onClick={() => del(p.id)} className="text-zinc-500 hover:text-rose-400"><Trash2 size={14}/></button>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
              <div className="text-zinc-500">Sconto base</div><div className="text-right font-mono text-emerald-600">{p.discount_pct}%</div>
              <div className="text-zinc-500">Margine min</div><div className="text-right font-mono">{p.min_margin_pct}%</div>
              <div className="text-zinc-500">Sconto max ruolo</div><div className="text-right font-mono">{p.max_discount_pct}%</div>
              <div className="text-zinc-500">IVA</div><div className="text-right font-mono">{(p.tax_rate*100).toFixed(0)}%</div>
            </div>
            <button onClick={() => toggle(p)} className={`mt-4 w-full text-xs rounded-lg py-1.5 font-semibold ${p.active ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-500"}`}>
              {p.active ? "● Attivo" : "○ Disattivo"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
